import React, { useState, useEffect } from 'react';
import ClipboardInput from './components/ClipboardInput';
import ClipboardHistory from './components/ClipboardHistory';
import Modal from './components/Modal';
import Menu from './components/Menu';
import About from './components/About';
import FAQs from './components/FAQs';
import Contact from './components/Contact';
import { createClipboard, fetchClipboardItems, addClipboardItem, deleteClipboardItem } from './services/supabaseClient';
import './styles/App.css';
import { supabase } from './services/supabaseClient';

const App = () => {
    const [history, setHistory] = useState([]);
    const [currentText, setCurrentText] = useState('');
    const [clipboardId, setClipboardId] = useState('');
    const [loading, setLoading] = useState(true);
    const [qrSrc, setQrSrc] = useState('');
    const [showAbout, setShowAbout] = useState(false);
    const [showFAQs, setShowFAQs] = useState(false);
    const [showContact, setShowContact] = useState(false);
    const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info', onConfirm: null });
    const [urlCopied, setUrlCopied] = useState(false);

    const showModal = (title, message, type = 'info', onConfirm = null) => {
        setModal({ isOpen: true, title, message, type, onConfirm });
    };

    const closeModal = () => {
        setModal({ isOpen: false, title: '', message: '', type: 'info', onConfirm: null });
    };

    const handleAddToHistory = (text) => {
        if (text.trim() && clipboardId) {
            const timestamp = new Date().toISOString();

            // Add to Supabase
            addClipboardItem(clipboardId, text, timestamp)
                .then((newItem) => {
                    // Update local state with the DB item
                    setHistory([
                        {
                            id: newItem.id,
                            text: newItem.text,
                            timestamp: newItem.timestamp || timestamp
                        },
                        ...history
                    ]);
                    setCurrentText('');
                })
                .catch((err) => {
                    console.error('Failed to add item to clipboard:', err);
                    showModal('Error', 'Failed to save item', 'info');
                });
        }
    };

    const handleDelete = async (id) => {
        // Remove from UI immediately
        setHistory(prevHistory => prevHistory.filter(item => item.id !== id));

        // Delete from database
        try {
            await deleteClipboardItem(id);
        } catch (err) {
            console.error('Failed to delete item from database:', err);
        }
    };

    const handleUpdate = (id, text) => {
        // Update the history with new text and timestamp
        const newTimestamp = new Date().toISOString();
        setHistory(prevHistory =>
            prevHistory.map(item =>
                item.id === id ? { ...item, text, timestamp: newTimestamp } : item
            )
        );
    };

    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setUrlCopied(true);
            setTimeout(() => {
                setUrlCopied(false);
            }, 3000);
        } catch (err) {
            console.error('Copy failed', err);
            showModal('Error', 'Failed to copy URL', 'info');
        }
    };

    // Initialize clipboard on mount
    useEffect(() => {
        const initializeClipboard = async () => {
            try {
                const params = new URLSearchParams(window.location.search);
                let clipId = params.get('id');

                if (!clipId) {
                    // Create a new clipboard
                    clipId = await createClipboard();
                    // Update URL with the clipboard ID
                    const newUrl = `${window.location.origin}${window.location.pathname}?id=${clipId}`;
                    window.history.replaceState({ clipboardId: clipId }, '', newUrl);
                }

                setClipboardId(clipId);

                // Fetch existing items from Supabase
                const items = await fetchClipboardItems(clipId);
                // Transform DB items to match local state format
                const transformedItems = items.map(item => ({
                    id: item.id,
                    text: item.text,
                    timestamp: item.timestamp || new Date().toISOString()
                }));
                setHistory(transformedItems);

                // Generate QR code with the clipboard URL
                const clipboardUrl = `${window.location.origin}${window.location.pathname}?id=${clipId}`;
                const qr = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(clipboardUrl)}`;
                setQrSrc(qr);

                setLoading(false);
            } catch (err) {
                console.error('Failed to initialize clipboard:', err);
                setLoading(false);
            }
        };

        initializeClipboard();
    }, []);

    if (loading) {
        return <div className="app-container"><p>Loading clipboard...</p></div>;
    }

    return (
        <div className="app-container">
            {showAbout && <About onClose={() => setShowAbout(false)} />}
            {showFAQs && <FAQs onClose={() => setShowFAQs(false)} />}
            {showContact && <Contact onClose={() => setShowContact(false)} />}
            <Modal
                isOpen={modal.isOpen}
                title={modal.title}
                message={modal.message}
                type={modal.type}
                onConfirm={modal.onConfirm ? modal.onConfirm : closeModal}
                onCancel={closeModal}
                confirmText="OK"
                cancelText="Cancel"
            />
            <div className="header">
                <div className="header-top">
                    <Menu onAboutClick={() => setShowAbout(true)} onFAQsClick={() => setShowFAQs(true)} onContactClick={() => setShowContact(true)} />
                </div>
                <img src="/logo.png" alt="CLIP AND GO Logo" className="header-logo" />
            </div>
            <div className="main-content">
                <div className="left-column">
                    {/* QR code for quick access from mobile devices */}
                    <div className="qr-section">
                        {qrSrc ? (
                            <>
                                <div style={{ marginBottom: '10px' }}>
                                    <span className="share-url">Scan QR Code or Copy URL to open on another device</span>
                                </div>
                                <div style={{ marginBottom: '10px' }}>
                                    <span className="share-url">Bookmark or Create a Shortcut for this URL —<br />It's your unique link to reuse anytime on your devices</span>
                                </div>
                                <img className="qr-image" src={qrSrc} alt="Scan to open on mobile" />
                                <div className="share-row">
                                    <button className="btn btn-secondary" onClick={handleCopyUrl}>
                                        {urlCopied ? 'Copied' : 'Copy Page URL'}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <p className="share-url">QR unavailable</p>
                        )}
                    </div>
                    <ClipboardInput onSubmit={handleAddToHistory} currentText={currentText} setCurrentText={setCurrentText} />
                </div>
                <div className="right-column">
                    <ClipboardHistory history={history} onDelete={handleDelete} onUpdate={handleUpdate} clipboardId={clipboardId} />
                </div>
            </div>
            <div className="footer">
                <span className="share-url">This Page URL will be deleted after 30 days of inactivity.</span>
            </div>
        </div>
    );
};

export default App;